import { Cell, SelectionBoundaries } from '@/models/GenericInterfaces'
import { Debouncer } from '@/utils/Debouncer'
import { clamp } from '@/utils/tools'
import { ComponentPublicInstance, computed, nextTick, readonly, ref } from 'vue'

export const useTableSelection = () => {
  // -----------
  // -- Setup --
  // -----------
  type SelectionConstrainment = 'row' | 'column'
  const dragSelectionDebouncer = new Debouncer()
  let currentSelectionGroup: string | undefined // used to limit selections (through drag/extend) to objects within the same selection group

  // ---------------
  // -- Reactives --
  // ---------------
  const selectionDraggingState = ref<SelectionConstrainment | 'normal' | false>(false)

  const selectionData = ref<{
    columnStart: number
    columnEnd: number
    rowStart: number
    rowEnd: number
  }>()

  // --------------------
  // -- Computed props --
  // --------------------
  const orderedSelectionData = computed((): SelectionBoundaries | undefined => {
    if (selectionData.value) {
      const topmostRow = Math.min(selectionData.value.rowStart, selectionData.value.rowEnd)
      const bottommostRow = Math.max(selectionData.value.rowStart, selectionData.value.rowEnd)

      const leftmostColumn = Math.min(selectionData.value.columnStart, selectionData.value.columnEnd)
      const rightmostColumn = Math.max(selectionData.value.columnStart, selectionData.value.columnEnd)

      return { leftmostColumn, rightmostColumn, topmostRow, bottommostRow }
    }
    return undefined
  })

  const selectedCells = computed(() => {
    const cells: Cell[] = []
    if (orderedSelectionData.value) {
      for (let row = orderedSelectionData.value?.topmostRow; row <= orderedSelectionData.value?.bottommostRow; row++) {
        for (let col = orderedSelectionData.value?.leftmostColumn; col <= orderedSelectionData.value?.rightmostColumn; col++) {
          cells.push({ rowIndex: row, columnIndex: col })
        }
      }
    }
    return cells
  })

  const activeCell = computed(() => {
    return selectionData.value
      ? {
          rowIndex: selectionData.value.rowStart,
          columnIndex: selectionData.value.columnStart,
        }
      : undefined
  })

  const multipleIndicesSelected = computed(() => {
    const selectedIndexCells = selectedCells.value.reduce((sum, cell) => (sum += cell.columnIndex === -1 ? 1 : 0), 0)
    return selectedIndexCells > 1
  })
  const singleRowIsSelected = computed(() => selectionData.value && selectionData.value?.rowStart === selectionData.value?.rowEnd)
  const singleColumnIsSelected = computed(() => selectionData.value && selectionData.value?.columnStart === selectionData.value?.columnEnd)

  // ---------------
  // -- Functions --
  // ---------------

  const clearSelected = () => {
    selectionData.value = undefined
  }

  const setCellSelection = (cell: Cell) => {
    selectionData.value = {
      columnStart: cell.columnIndex,
      columnEnd: cell.columnIndex,
      rowStart: cell.rowIndex,
      rowEnd: cell.rowIndex,
    }
  }

  const startSelectionDrag = (
    cell?: Cell,
    constrainment?: SelectionConstrainment,
    dragEndCallback?: () => void,
    selectionLimit?: string,
    event?: MouseEvent,
  ) => {
    if (!selectionDraggingState.value && !event?.shiftKey) {
      selectionDraggingState.value = constrainment || 'normal'
      currentSelectionGroup = selectionLimit

      if (cell) {
        // Timeout stops accidental selection drags if the cell size changes when the view is re-rendered due to the new selection
        // nextTick().then(() => setCellSelection(cell))
        setTimeout(async () => setCellSelection(cell))
      }

      document.addEventListener(
        'mouseup',
        () => {
          dragEndCallback?.()
          selectionDraggingState.value = false
        },
        { once: true },
      )
    }
  }

  const handleSelectionDrag = (cell: Cell, selectionLimit?: string) => {
    if (selectionDraggingState.value && (!currentSelectionGroup || !selectionLimit || selectionLimit === currentSelectionGroup)) {
      dragSelectionDebouncer.debounce(() => {
        extendSelectionTo(cell, selectionLimit)
      }, 5)
    }
  }

  /**
   * Extends current selection to given cell
   * @param cell
   * @param selectionLimit
   */
  const extendSelectionTo = (cell: Cell, selectionLimit?: string) => {
    if (selectionData.value && (!currentSelectionGroup || !selectionLimit || selectionLimit === currentSelectionGroup)) {
      selectionData.value.rowEnd = cell.rowIndex
      if (selectionDraggingState.value !== 'column') {
        selectionData.value.columnEnd = cell.columnIndex
      }
    }
  }

  const abortSelectionDrag = () => {
    if (selectionDraggingState.value && activeCell.value) {
      setCellSelection(activeCell.value)
    }

    selectionDraggingState.value = false
  }

  const cellIsSelected = (cell?: Cell) => cell && rowIsSelected(cell.rowIndex) && columnIsSelected(cell.columnIndex)

  const rowIsSelected = (rowIndex: number) =>
    orderedSelectionData.value && orderedSelectionData.value.topmostRow <= rowIndex && orderedSelectionData.value.bottommostRow >= rowIndex

  const columnIsSelected = (columnIndex: number) =>
    orderedSelectionData.value && orderedSelectionData.value.leftmostColumn <= columnIndex && orderedSelectionData.value.rightmostColumn >= columnIndex

  const navigateTable = async (deltaX = 0, deltaY = 0, maxRowIndex: number, maxColumnIndex: number) => {
    if (activeCell.value) {
      const cell = {
        rowIndex: clamp(activeCell.value.rowIndex + deltaY, 0, maxRowIndex),
        columnIndex: clamp(activeCell.value.columnIndex + deltaX, 0, maxColumnIndex),
      }

      return new Promise((resolve) => {
        dragSelectionDebouncer.debounce(() => {
          setCellSelection(cell)
          resolve(0)
        }, 10)
      })
    }
  }

  /**
   * Expands or contracts current selection by a number of cells controlled by deltaX and deltaY
   * @param deltaX negative number to expand/contract to the left, positive for right, 0 for no change
   * @param deltaY negative number to expand/contract upwards, positive for downwards, 0 for no change
   * @param maxRowIndex sets maximum row selection limit
   * @param maxColumnIndex sets maximum column selection limit
   * @param minRowIndex sets minmimum row selection limit
   * @param minColumnIndex sets minmimum column selection limit
   * @returns
   */
  const expandOrContractSelection = (deltaX = 0, deltaY = 0, maxRowIndex: number, maxColumnIndex: number, minRowIndex = 0, minColumnIndex = 0) => {
    if (selectionData.value) {
      const cell = {
        rowIndex: clamp(selectionData.value.rowEnd + deltaY, minRowIndex, maxRowIndex),
        columnIndex: clamp(selectionData.value.columnEnd + deltaX, minColumnIndex, maxColumnIndex),
      }

      dragSelectionDebouncer.debounce(() => {
        extendSelectionTo(cell)
      }, 10)
      return cell
    }
  }

  return {
    selectionDraggingState: readonly(selectionDraggingState),

    // Computed
    activeCell,
    selectedCells,
    orderedSelectionData,
    multipleIndicesSelected,
    singleColumnIsSelected,
    singleRowIsSelected,
    // Functions
    clearSelected,
    setCellSelection,
    startSelectionDrag,
    handleSelectionDrag,
    extendSelectionTo,
    cellIsSelected,
    rowIsSelected,
    columnIsSelected,
    isRightmostSelectedCell: (cell: Cell) => cellIsSelected(cell) && cell?.columnIndex === orderedSelectionData.value?.rightmostColumn,
    // the singular selected cell or the origin cell where a selection drag started
    isActiveCell: (cell?: Cell) =>
      selectionData.value && selectionData.value.columnStart === cell?.columnIndex && selectionData.value.rowStart === cell?.rowIndex,
    isSoleSelectedCell: (cell?: Cell) => cellIsSelected(cell) && selectedCells.value.length === 1,
    navigateTable,
    expandOrContractSelection,
    abortSelectionDrag,
  }
}
